import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { contabilizacaoService } from "../services/contabilizacaoService";

const KEYS = {
  lancamentos: (params: object) => ["contabil", "lancamentos", params] as const,
  parametrizacoes: (empresaId: number) => ["contabil", "parametrizacoes", empresaId] as const,
  razao: (contaId: number, ini: string, fim: string) => ["contabil", "razao", contaId, ini, fim] as const,
  balancete: (empresaId: number, ini: string, fim: string) => ["contabil", "balancete", empresaId, ini, fim] as const,
  dre: (empresaId: number, ini: string, fim: string) => ["contabil", "dre", empresaId, ini, fim] as const,
  balanco: (empresaId: number, dt: string) => ["contabil", "balanco", empresaId, dt] as const,
};

export function useLancamentos(params: {
  dtInicio?: string; dtFim?: string; contaId?: number;
  operacaoId?: number; tipoOperacao?: number;
}) {
  return useQuery({
    queryKey: KEYS.lancamentos(params),
    queryFn: () => contabilizacaoService.listarLancamentos(params),
  });
}

export function usePartidas(params: {
  dtInicio?: string; dtFim?: string; contaId?: number;
  tipoOperacao?: number; numero?: number;
  pagina: number; tamanhoPagina: number;
}) {
  return useQuery({
    queryKey: ["contabil", "partidas", params],
    queryFn: () => contabilizacaoService.consultarPartidas(params),
    placeholderData: keepPreviousData,
  });
}

export function useCriarLancamentoManual() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: contabilizacaoService.criarLancamentoManual,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contabil"] });
      toast.success("Lançamento manual registrado.");
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { mensagem?: string } } }).response?.data?.mensagem
        ?? "Falha ao registrar lançamento.";
      toast.error(msg);
    },
  });
}

export function useEstornarLancamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ num, motivo }: { num: number; motivo: string }) =>
      contabilizacaoService.estornarLancamento(num, motivo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contabil"] });
      toast.success("Lançamento estornado.");
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { mensagem?: string } } }).response?.data?.mensagem
        ?? "Falha ao estornar lançamento.";
      toast.error(msg);
    },
  });
}

export function useParametrizacoes(empresaId: number) {
  return useQuery({
    queryKey: KEYS.parametrizacoes(empresaId),
    queryFn: () => contabilizacaoService.listarParametrizacoes(empresaId),
    enabled: empresaId > 0,
  });
}

export function useSalvarParametrizacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: contabilizacaoService.salvarParametrizacao,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contabil", "parametrizacoes"] });
      toast.success("Parametrização salva.");
    },
  });
}

export function useRemoverParametrizacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: contabilizacaoService.removerParametrizacao,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contabil", "parametrizacoes"] });
      toast.success("Parametrização removida.");
    },
  });
}

export function useRazao(contaId: number, dtInicio: string, dtFim: string) {
  return useQuery({
    queryKey: KEYS.razao(contaId, dtInicio, dtFim),
    queryFn: () => contabilizacaoService.razao(contaId, dtInicio, dtFim),
    enabled: contaId > 0 && !!dtInicio && !!dtFim,
  });
}

export function useBalancete(empresaId: number, dtInicio: string, dtFim: string) {
  return useQuery({
    queryKey: KEYS.balancete(empresaId, dtInicio, dtFim),
    queryFn: () => contabilizacaoService.balancete(empresaId, dtInicio, dtFim),
    enabled: empresaId > 0 && !!dtInicio && !!dtFim,
  });
}

export function useDre(empresaId: number, dtInicio: string, dtFim: string) {
  return useQuery({
    queryKey: KEYS.dre(empresaId, dtInicio, dtFim),
    queryFn: () => contabilizacaoService.dre(empresaId, dtInicio, dtFim),
    enabled: empresaId > 0 && !!dtInicio && !!dtFim,
  });
}

export function useBalanco(empresaId: number, dtPosicao: string) {
  return useQuery({
    queryKey: KEYS.balanco(empresaId, dtPosicao),
    queryFn: () => contabilizacaoService.balanco(empresaId, dtPosicao),
    enabled: empresaId > 0 && !!dtPosicao,
  });
}
